import { Component, Show, createResource, createSignal } from "solid-js";
import { A } from "@solidjs/router";
import { openPolarPortal, startPolarCheckout } from "../auth";
import { useAuth } from "../App";

type Currency = "EUR" | "USD";
type FxRate = { base: "EUR"; quote: "USD"; rate: number; date: string; source: string };

const MONTHLY_EUR = 29.99;
const ANNUAL_EUR = 199.99;

const PricingCards: Component = () => {
  const auth = useAuth();
  const [currency, setCurrency] = createSignal<Currency>("EUR");
  const [checkoutPlan, setCheckoutPlan] = createSignal<"monthly" | "annual" | null>(null);
  const [checkoutError, setCheckoutError] = createSignal<string | null>(null);
  const [fx] = createResource<FxRate>(async () => {
    const response = await fetch("/api/fx/eur-usd");
    if (!response.ok) throw new Error("Rate unavailable");
    return response.json();
  });

  const beginCheckout = async (plan: "monthly" | "annual") => {
    if (!auth().user_id) {
      window.location.assign("/login?mode=signup&next=pricing");
      return;
    }
    setCheckoutPlan(plan);
    setCheckoutError(null);
    try {
      if (auth().is_paid) await openPolarPortal();
      else await startPolarCheckout(plan);
    } catch (error) {
      setCheckoutError((error as Error).message);
      setCheckoutPlan(null);
    }
  };

  const price = (euros: number) => {
    if (currency() === "EUR") {
      return new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format(euros);
    }
    if (!fx()?.rate) return "—";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(euros * fx()!.rate);
  };

  return (
    <>
      <div class="currency-control">
        <span>Display currency</span>
        <div class="currency-toggle" role="group" aria-label="Display currency">
          <button type="button" classList={{ active: currency() === "EUR" }} aria-pressed={currency() === "EUR"} onClick={() => setCurrency("EUR")}>€ EUR</button>
          <button type="button" classList={{ active: currency() === "USD" }} aria-pressed={currency() === "USD"} disabled={!fx()?.rate} title={fx.error ? "Live USD rate is temporarily unavailable" : "Show prices in US dollars"} onClick={() => setCurrency("USD")}>$ USD</button>
        </div>
      </div>

      <div class="pricing-grid">
        <article class="price-card">
          <div><span class="plan-name">MONTHLY</span><h3>Stay flexible.</h3><p>Full platform access, billed monthly.</p></div>
          <div class="price"><strong>{price(MONTHLY_EUR)}</strong><span>/ month</span></div>
          <p class="plan-legal">Renews monthly until cancelled.</p>
          <ul><li>Unlimited race cards</li><li>Live statistical edges</li><li>Odds and market movement</li><li>Model probability comparisons</li></ul>
          <button class="button button-dark button-wide" type="button" disabled={!!checkoutPlan()} onClick={() => void beginCheckout("monthly")}>{checkoutPlan() === "monthly" ? "Opening Polar…" : auth().is_paid ? "Manage current plan" : "Choose monthly"} <span>↗</span></button>
        </article>
        <article class="price-card price-card-featured">
          <div class="best-value">SAVE 44%</div>
          <div><span class="plan-name">ANNUAL</span><h3>Back the process.</h3><p>Our best rate for committed analysts.</p></div>
          <div class="price"><strong>{price(ANNUAL_EUR)}</strong><span>/ year</span></div>
          <p class="plan-legal">Renews annually until cancelled.</p>
          <ul><li>Everything in Monthly</li><li>Save 44% versus monthly</li><li>Priority feature access</li><li>One annual payment</li></ul>
          <button class="button button-primary button-wide" type="button" disabled={!!checkoutPlan()} onClick={() => void beginCheckout("annual")}>{checkoutPlan() === "annual" ? "Opening Polar…" : auth().is_paid ? "Manage current plan" : "Choose annual"} <span>↗</span></button>
        </article>
      </div>

      <Show when={checkoutError()}><p class="form-error pricing-checkout-error">{checkoutError()}</p></Show>

      <div class="pricing-note pricing-rate-note">
        <Show when={currency() === "USD" && fx()} fallback={<>Plans are billed in EUR. Secure checkout by Polar.</>}>
          Approximate USD display using the {fx()!.source} reference rate from {fx()!.date}: €1 = ${fx()!.rate.toFixed(4)}. Final card conversion may vary.
        </Show>
        {!auth().user_id && <> <A href="/login?next=pricing">Log in</A> or create an account before checkout.</>}
        <span> By purchasing, you agree to the <A href="/terms">Terms</A> and acknowledge the <A href="/refunds">Cancellation and Refund Policy</A>, including applicable 14-day withdrawal rights.</span>
      </div>
    </>
  );
};

export default PricingCards;
